// WHAT A SCREEN ASKS RIKSU FOR, declared rather than written.
//
// Riksu already has five declarations — `Column`, `Flow`/`Field`, `Source`,
// `creations.ts`, `destinations.ts` — and every one follows the same rule: the call site
// declares WHAT, the mechanism decides HOW, and CI walks the declaration. Reads were the
// one thing left where the call site wrote the HOW, which is why the shape below had been
// privately re-invented in `assets.tsx`, `setup.tsx`, `leave-setup.tsx` and
// `expense-creates.tsx`, twice under the same name `useRead` and with two incompatible
// failure semantics.
//
// This file holds the TYPES a module declares in. It has no runtime beyond an identity
// function: the store is `reads.ts`, and keeping the vocabulary apart from the machinery
// is what lets a reviewer read every read in the product without reading the cache.
//
// # THE ADDRESS IS THE CONTRACT'S, NOT A STRING
//
// `at` is a member of a union derived from `contract.ts`, so a typo is a compile error
// rather than a 404 somebody finds on a screen; `Answer` is read off the same document's
// own `200`, so `call<Person[]>("/v1/people")` — a hand-written assertion nothing checks —
// stops being writable. The holes and the query parameters come from the same place, so
// an address is either complete or it does not compile.
//
// # WHAT MAY BE HELD IS NOT THIS FILE'S DECISION
//
// A declaration may ask to be held past its last reader (`holds`). Whether it is ALLOWED
// to is derived from `RouteSpec.subject` in the backend's own route table, by
// `tools/check-reads.mjs` — `Impersonal` and nothing else. A catalogue may be held; a list
// that names people may not, and no reviewer has to remember which is which.

import type { paths } from "./contract";

/**
 * Every address riksu answers a GET at, as the contract spells it.
 *
 * Derived rather than listed: a read of a path riksu does not serve cannot be declared,
 * and a route that goes away takes its declarations red with it.
 */
export type Address = {
  [P in keyof paths]: paths[P] extends { get: object } ? P : never;
}[keyof paths];

/**
 * Every write riksu serves, as `"POST /v1/…"` — the vocabulary `staleAfter` speaks.
 *
 * A STRING and deliberately not an imported binding. Two of the three designs this
 * decision came out of proposed naming a write by binding so a rename breaks the
 * invalidation at `tsc`; that argument is right about READS and does not transfer here,
 * because a write is not declared in TypeScript at all. `Flow.path` + `Flow.method` and
 * the literal `call` argument already declare every one of them, and inventing a third
 * vocabulary to be renamed is the drift this family of files exists to prevent. What is
 * bought instead is the same guarantee by another road: this union comes off the contract,
 * so a `staleAfter` naming a route riksu stopped serving is a compile error.
 */
export type Written = {
  [P in keyof paths]: {
    [M in Extract<keyof paths[P], "post" | "put">]: paths[P][M] extends object
      ? `${Uppercase<M & string>} ${P & string}`
      : never;
  }[Extract<keyof paths[P], "post" | "put">];
}[keyof paths];

/**
 * What the 200 carries, off the contract's own document.
 *
 * A screen never writes this type and therefore cannot get it wrong.
 */
export type Answer<A extends Address> = paths[A] extends {
  get: { responses: { 200: { content: { "application/json": infer B } } } };
}
  ? B
  : never;

/**
 * The `{name}` holes in an address, each one required at the call site.
 *
 * Read off the template rather than off the contract's `parameters.path`, because the
 * template is what `fill` substitutes into and two sources for one fact is the drift a
 * declaration exists to prevent.
 */
export type Holes<S extends string> = S extends `${string}{${infer N}}${infer After}`
  ? { readonly [K in N]: string } & Holes<After>
  : Record<never, never>;

/**
 * The query parameters riksu declares for this read, or `never` where it declares none.
 *
 * NOT AN AFTERTHOUGHT, and the pilot module is why. `/v1/assets?as_of=` and
 * `/v1/assets/holdings?as_of=` are the zimmet screen's two lists, `/v1/leave/cost` and
 * `/v1/expense/limits` are the two figures somebody submits against, and every one of them
 * is a query read. A declaration that could only express `{hole}` addresses would have
 * been unable to describe the screens it was written for — which is what makes this a
 * member rather than an escape hatch back to a raw string.
 */
export type Query<A extends Address> = paths[A] extends {
  get: { parameters: { query?: infer Q } };
}
  ? [NonNullable<Q>] extends [never]
    ? never
    : NonNullable<Q>
  : never;

/**
 * WHAT A REFUSAL LOOKS LIKE — the read's decision, never the screen's.
 *
 * This member exists because riksu had already answered it two different ways and could
 * not say so: `assets.tsx` surfaces a failure with `onRetry`, and `flow.tsx`'s `useSources`
 * deliberately renders an empty picker ("A SOURCE THAT REFUSES LEAVES AN EMPTY PICKER, not
 * a broken form"). Both are right. What was wrong is that each mechanism re-decided it, and
 * that the two hooks named `useRead` disagreed — one kept the failure and one returned
 * `null` and lost it.
 *
 * It changes the TYPE rather than only the behaviour: `Load` has a `failed` arm only for
 * `surface`. So a picker cannot render a failure it declared it does not want, and a table
 * cannot forget one.
 *
 * A third posture — render nothing at all, which is `profile.tsx`'s rule for a section —
 * is deliberately NOT here yet. It has no caller in this module, and a variant with no call
 * site is exactly what the `SHARED` and `HEADLESS` staleness rules in `check-parity.mjs`
 * refuse one level along. It arrives with `profile.tsx`.
 */
export type Refusal = "surface" | "empty";

/** How long an answer is held after its last reader goes. Absent means it is not. */
export type Holds = {
  /**
   * Held until a declared write drops it.
   *
   * Only ever honest for a catalogue whose staleness costs a missing option somebody can
   * fix with F5 — and `tools/check-reads.mjs` refuses it on any read whose route does not
   * declare `Subject::Impersonal`, so "is this a catalogue" is answered from the backend's
   * own route table rather than by whoever wrote the line.
   */
  readonly until: "invalidated";
  /** Why this one may be held. Prose, read in review; the gate checks it is a sentence. */
  readonly because: string;
};

interface Declared<A extends Address, F extends Refusal> {
  /**
   * The address. Named `at` and not `path` on purpose: `check-parity.mjs:520` skips any
   * match preceded by `path:` — the exemption that stops `Source` declarations counting as
   * screens drawing a fact — and a read declared with `path:` would be silently swallowed
   * by the gate that is supposed to see it.
   */
  readonly at: A;
  readonly refusal: F;
  /**
   * Which writes make this stale. The mechanism re-reads; no screen threads a prop.
   *
   * Declared on the READ rather than on the write, because adding a read is the operation
   * people perform most and this is the side that makes it one line: the edge is inside the
   * declaration you are already typing. The reverse — a write naming the reads it moves —
   * costs an edit to every write each time a read is added, and nothing makes anybody write
   * it.
   */
  readonly staleAfter?: readonly Written[];
  /** Held past its last reader. See `Holds`; CI decides whether it is allowed. */
  readonly holds?: Holds;
  // `shared` WAS HERE — "why more than one screen reads this, the `SHARED` reason beside
  // the read" — and it is gone on 2026-08-08 rather than corrected. It is the only member
  // this interface ever carried that drove NO behaviour: `refusal` changes the type,
  // `staleAfter` moves the entry, `holds` decides a lifetime, and this was a note to a gate.
  //
  // It was also a second declaration of a fact `tools/check-parity.mjs`'s `SHARED` map
  // already held, and the two disagreed. `/v1/assets/holdings` is the case: this file said
  // TWO QUESTIONS and named the zimmet screen and the record pane; `SHARED` named those two
  // AND `profile.tsx`. The settled-sounding one was the incomplete one, and the reason is
  // structural rather than careless — **a `shared` member is a claim about a set this file
  // cannot see.** `check-reads.mjs` counted readers by resolving the IMPORT, and `profile.tsx`
  // imports nothing here; it fetches the address by hand and is recorded as the sweep. So the
  // sentence was written in the one place the third reader is invisible.
  //
  // Two more things settled it. The claim is about an ADDRESS and the member sat on a
  // DECLARATION, so half of them carried no information at all — "The picker half of the pair
  // `catalogue` describes" — being pointers to the sibling that held the real sentence. And
  // `SHARED` covers every address riksu serves, where this covers only migrated modules:
  // `GET /v1/people/{person_id}` has ten readers, a reason, and no declaration at all.
  //
  // So the reason lives where the readers are COUNTED, which is the gate that can see all of
  // them. Deleting the copy is what makes a contradiction impossible; a cross-check would
  // have left two texts that still both have to be maintained.
}

/**
 * A read whose refusal is riksu's problem document and a way back.
 *
 * Any answer shape: a table's rows, one record, a figure.
 */
export interface Surfaced<A extends Address> extends Declared<A, "surface"> {}

/**
 * A read whose refusal is an EMPTY ANSWER — a picker the caller may not list is an empty
 * picker, not a broken form.
 *
 * Constrained to an ARRAY answer, and that is the point of splitting the interface: there
 * is no empty `LeaveBalance` and no empty `RecoveryCeiling`, so declaring `empty` on one
 * would have to invent a value riksu never sent. The kind of the declaration decides what
 * the declaration is permitted to say, which is `Column.kind` deciding a comparator, moved
 * to the type level.
 */
export interface Emptied<A extends Address> extends Declared<A, "empty"> {
  readonly at: Answer<A> extends readonly unknown[] ? A : never;
}

export type Read<A extends Address, F extends Refusal> = F extends "surface"
  ? Surfaced<A>
  : Emptied<A>;

/** Any declaration, for the places that hold a list of them. */
export type AnyRead = Surfaced<Address> | Emptied<Address>;

/**
 * The declaration, and NOTHING BESIDE IT.
 *
 * `R extends AnyRead` alone does not refuse a stray member, and that was measured rather
 * than assumed. Deleting `shared` from `Declared` was supposed to make writing one a
 * compile error; it was not. A `read({ …, shared: "…" })` planted at a real call site
 * compiled **completely clean** — an object type with an extra property still satisfies
 * `extends AnyRead`, and TypeScript's excess-property check does not reach an object literal
 * inferred through a generic constraint. So the member would have gone on being writable
 * while `tools/check-reads.mjs` no longer parsed it: a declaration that reads as policy,
 * costs a review comment, and governs nothing.
 *
 * That is worse than leaving it in place, and it is the shape this repository keeps paying
 * for — a signal claiming a completeness it does not have. `Exclude<keyof R, keyof AnyRead>`
 * is every member the declaration carries that this file does not name, and requiring each
 * one to be `never` is what makes a string there a type error at the call site rather than
 * silence. It is general on purpose: it refuses the NEXT stray member as well as this one,
 * including a misspelling of a real one, which `staleAfer` would otherwise have been.
 */
type Exactly<R> = AnyRead & Record<Exclude<keyof R, keyof AnyRead>, never>;

/** The declarations loaded so far; the read mechanism consumes this registry one-way. */
const declarations = new Set<AnyRead>();

export function registeredReads(): ReadonlySet<AnyRead> {
  return declarations;
}

/**
 * Declares one read.
 *
 * `const` on the parameter is what keeps `at` a literal rather than widening it to
 * `string`, which is what makes `Answer`, `Holes` and `Query` resolvable at the call site.
 *
 * IT ALSO REGISTERS, and that is a correctness requirement rather than a convenience. A
 * write is matched against every declaration, so a declaration the store has not been told
 * about is a read no write can ever invalidate — silently, with the screen looking fine
 * until somebody records something and the list does not move. Registering HERE means a
 * declaration is known the moment its module file is loaded, which is the moment a screen
 * imports it; the alternative was a registry somebody has to remember to import, and the
 * first screen to write `import { assets } from "../reads/assets"` would have bypassed it.
 * `src/reads/index.ts` stays as the list a reviewer and CI read, not as the wiring.
 */
export function read<const R extends Exactly<R>>(declared: R): R {
  declarations.add(declared);
  return declared;
}

/**
 * What a read still needs to become an address.
 *
 * `path` is required exactly when the template has holes; `query` is always optional,
 * because every query parameter riksu declares is itself optional and a read with none
 * supplied is a legitimate address rather than an incomplete one.
 */
export type Where<A extends Address> = ([keyof Holes<A>] extends [never]
  ? { readonly path?: undefined }
  : { readonly path: Holes<A> }) &
  ([Query<A>] extends [never]
    ? { readonly query?: undefined }
    : { readonly query?: Partial<Query<A>> });

/** Whether the call site MUST pass a `Where` — that is, whether the address has holes. */
export type NeedsWhere<A extends Address> = [keyof Holes<A>] extends [never] ? false : true;

/**
 * What riksu has said about this read.
 *
 * `refreshing` carries the PREVIOUS answer, which is what makes "a table does not blink
 * back to skeletons after every create" expressible rather than a convention each screen
 * remembers. It is `Quotation<T>` from `ui/quoted.tsx` with the list vocabulary the other
 * sixteen files already wrote, and `showing`/`isSettled` are the same two functions over it.
 *
 * The `failed` arm is present only for `refusal: "surface"`.
 */
export type Load<T, F extends Refusal = Refusal> =
  | { readonly status: "idle" }
  | { readonly status: "loading" }
  | { readonly status: "refreshing"; readonly previous: T }
  | { readonly status: "loaded"; readonly value: T }
  | (F extends "surface" ? { readonly status: "failed"; readonly failure: unknown } : never);

/**
 * The value to SHOW right now: the current answer, or the last one while a newer is in
 * flight.
 *
 * `ui/quoted.tsx`'s `showing`, for the list side. It is here rather than written out at
 * each call site because "keep the previous while a newer one is in flight" is exactly the
 * line that gets forgotten — and forgotten, a table empties itself every time somebody
 * records something.
 */
export function showing<T>(load: Load<T, Refusal>): T | undefined {
  switch (load.status) {
    case "loaded":
      return load.value;
    case "refreshing":
      return load.previous;
    default:
      return undefined;
  }
}
